import { useEffect } from 'react';
import { Routes, Route, useNavigate, Navigate, useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Wrapper from '@/pages/wrapper';
import { isAuthenticated } from '@/services/session-service';
import '@mantine/core/styles.css';

import {
    RESERVATION_PATH,
    RESERVATION_PRINT_PATH,
    PERSON_PATH,
    PERSONS_PATH,
    BUSINESS_PATH,
    BUSINESSES_PATH,
    MY_BUSINESSES_PATH,
    SERVICES_PATH,
    TARIFFS_PATH,
    SERVICE_PATH,
    initNavigate,
    MANIFEST_SERVICE_ORDER_PATH,
    EDIT_SERVICE_ORDER_PATH,
    SERVICE_GROUPS_PATH,
    SERVICE_GROUP_PATH,
    PRODUCTS_PATH,
    PRODUCT_PATH,
    LOGIN_PATH,
    ONBOARDING_PATH,
    RESERVATIONS_PATH,
    RESERVATIONS_EXPORT_PATH,
    SETTINGS_PATH,
    SETTINGS_USERS_PATH,
    SETTINGS_USER_EDIT_PATH,
    INVENTORY_ITEMS_PATH,
    METRICS_PATH,
    PAYMENTS_PATH,
    PAYMENTS_INCOME_PATH,
    PAYMENTS_OUTCOME_PATH,
    AGENDA_PATH,
    MONEY_TRANSACTIONS_PATH,
    MONEY_TRANSACTIONS_IN_PATH,
    MONEY_TRANSACTIONS_OUT_PATH,
    SUBSCRIPTIONS_PATH,
    FINANCES_PATH,
    AI_CHAT_PATH,
    TICKETS_PATH,
    TICKET_PATH,
    SETTINGS_ONLINE_STORE_PATH,
    SETTINGS_LANGUAGE_PATH,
    SETTINGS_EXCHANGE_RATES_PATH,
    SETTINGS_DISCOUNT_CATEGORIES_PATH,
    SETTINGS_SERVICE_ORDERS_PATH,
    SETTINGS_CHANGE_PASSWORD_PATH,
    SETTINGS_TAX_SETTINGS_PATH,
    SETTINGS_NOTIFICATIONS_PATH,
    SETTINGS_EMAILS_PATH,
    SETTINGS_GMAIL_PATH,
    AFIP_HISTORY_PATH,
    AFIP_GENERATE_PATH,
    OPERATIONS_PATH,
    FLIGHTS_PATH,
    ADMIN_HOME_PATH,
    SERVICE_ORDERS_PATH,
    SERVICE_BOOKING_PATH,
    BALANCE_PATH,
    ISSUES_PATH,
    HISTORY_PATH,
    EMAIL_CAMPAIGNS_PATH,
    EMAIL_TEMPLATES_PATH,
    EMAIL_TEMPLATE_PATH,
    ASSIGNMENTS_PATH,
    CREATE_ACCOUNT_PATH,
} from './lib/navigation';

// Public pages
import LoginPage from './pages/login-page';
import OnboardingPage from './pages/onboarding-page';
import CreateAccountPage from './pages/create-account-page';

// Private pages
import ReservationPage from '@/pages/reservation-page';
import PersonPage from '@/pages/edit-person-page';
import ServicePage from '@/pages/edit-service-page';
import ListBusinessessPage from './pages/businesses-page';
import HomePage from './pages/home-page';
import EditBusinessPage from './pages/edit-business-page';
import ManifestServiceOrderPage from './pages/manifest-service-order-page';
import EditServiceOrderPage from './pages/edit-service-order-page';
import ReservationsExportPage from './pages/reservations-export-page';
import ServiceGroupsPage from './pages/service-groups-page';
import EditServiceGroupPage from './pages/edit-service-group-page';
import MetricsPage from './pages/metrics-page';
import InventoryItemsPage from './pages/inventory-page';
import UsersPage from './pages/users-page';
import EditUserPage from './pages/edit-user-page';
import UserDetailsTab from './components/user-edit/user-details-tab';
import UserMenuPermissionsTab from './components/user-edit/user-menu-permissions-tab';
import UserCreatePermissionsTab from './components/user-edit/user-create-permissions-tab';
import UserReservationPermissionsTab from './components/user-edit/user-reservation-permissions-tab';
import ServiceOrdersPage from './pages/service-orders-page';
import ServicesListPage from './pages/services-page';
import MoneyTransactionsPage from './pages/money-transactions-page';
import PaymentsListPage from './pages/payments-page';
import EditPaymentPage from './pages/edit-payment-page';
import AgendaPage from './pages/agenda-page';
import MoneyTransactionsInPage from './pages/money-transactions-in-page';
import MoneyTransactionsOutPage from './pages/money-transactions-out-page';
import SubscriptionsPage from './pages/subscriptions-page';
import EditSubscriptionPage from './pages/edit-subscription-page';
import FinancesPage from './pages/finances-page';
import StaffPage from './pages/staff-page';
import PaxsPage from './pages/persons-page';
import HistoryPage from './pages/history-page';
import AiChatPage from './pages/ai-chat-page';
import TicketsPage from './pages/tickets-page';
import EditTicketPage from './pages/edit-ticket-page';
import ServiceOrdersMatrixPage from './pages/service-orders-matrix-page';
import FlightsPage from './pages/flights-page';
import BalancePage from './pages/balance-page';
import IssuesPage from './pages/issues-page';
import EmailCampaignsPage from './pages/email-campaigns-page';
import GoogleCallbackPage from './pages/google-callback-page';

// Settings pages
import TenantSettingsPage from './pages/settings/tenant-settings';
import SettingsOnlineStorePage from './pages/settings/online-store';
import SettingsLanguagePage from './pages/settings/language';
import SettingsExchangeRatesPage from './pages/settings/exchange-rates';
import SettingsDiscountCategoriesPage from './pages/settings/discount-categories';
import SettingsServiceOrdersPage from './pages/settings/service-orders';
import SettingsTaxSettingsPage from './pages/settings/tax-settings';
import GenerateInvoicePage from './pages/generate-invoice-page';
import AfipHistoryPage from './pages/afip-history-page';
import EditServiceDetails from '@/components/edit-service-details';
import EditServiceOperations from '@/components/edit-service-operations';
import ServiceImagesManager from '@/components/service-images-manager';
import ServiceProviders from '@/components/service-providers';
import EditServiceCosts from '@/components/prices/edit-service-costs-tab';
import EditServicePrices from '@/components/prices/edit-service-prices-tab';
import EditServicePricesAndCosts from '@/components/prices/edit-service-prices-and-costs-tab';
import EditServiceDiscounts from '@/components/edit-service-discounts';
import EditServiceLimits from '@/components/edit-service-limits';
import ServiceBookingsTab from '@/components/service-bookings-tab';
import type { Service, Business, Person } from '@turizi/shared';
import EditBusinessDetails from '@/components/edit-business-details';
import BusinessUsersTab from '@/components/business-users-tab';
import BusinessEmailsTab from '@/components/business-emails-tab';
import BusinessCommercialTab from '@/components/business-commercial-tab';
import BalanceTab from '@/components/balance-tab';
import BusinessPaymentsTab from '@/components/business-payments-tab';
import BusinessReservationsAsClientTab from '@/components/business-reservations-as-client-tab';
import BusinessBookingsAsProviderTab from '@/components/business-bookings-as-provider-tab';
import BusinessDeletedReservationsTab from '@/components/business-deleted-reservations-tab';
import ProviderServices from '@/components/provider-services';
import { useUpdateBusiness } from '@/hooks/api-hooks';
import PersonDetailsTab from '@/components/paxs/person-details-tab';
import PersonReservationsTab from '@/components/paxs/person-reservations-tab';
import ServiceBookingPage from './pages/service-booking-page';
import NotFoundPage from './pages/not-found-page';
import EmailTemplatesPage from './pages/email-templates-page';
import ProductsPage from './pages/products-page';
import ProductEditPage from './pages/product-edit-page';
import EditEmailTemplatePage from './pages/edit-email-template-page';
import AssignmentsPage from './pages/assignments-page';
import SettingsChangePasswordPage from './pages/settings/change-password';
import SettingsNotificationsPage from './pages/settings/notifications';
import SettingsEmailsPage from './pages/settings/emails';
import SettingsGmailPage from './pages/settings/gmail-settings';
import TariffsPage from './pages/tariffs-page';
import ReservationPrintPage from './pages/reservation-print-page';
import ServicePriceGeneratorTab from './components/prices/service-price-generator-tab';

const ServiceDetailsRoute = () => {
    const service = useOutletContext<Service>();
    return <EditServiceDetails service={service} />;
};

const ServiceOperationsRoute = () => {
    const service = useOutletContext<Service>();
    return <EditServiceOperations service={service} />;
};

const ServiceProvidersRoute = () => {
    const service = useOutletContext<Service>();
    return <ServiceProviders service={service} />;
};

const ServiceCostsRoute = () => {
    const service = useOutletContext<Service>();
    return <EditServiceCosts service={service} />;
};

const ServicePricesRoute = () => {
    const service = useOutletContext<Service>();
    return <EditServicePrices service={service} />;
};

const ServiceDiscountsRoute = () => {
    const service = useOutletContext<Service>();
    return <EditServiceDiscounts service={service} />;
};

const ServiceLimitsRoute = () => {
    const service = useOutletContext<Service>();
    return <EditServiceLimits service={service} />;
};

const ServicePriceGeneratorRoute = () => {
    const service = useOutletContext<Service>();
    return <ServicePriceGeneratorTab service={service} />;
};

const ServicePricesAndCostsRoute = () => {
    const service = useOutletContext<Service>();
    return <EditServicePricesAndCosts service={service} />;
};

const ServiceImagesRoute = () => {
    return <ServiceImagesManager />;
};

const ServiceBookingsRoute = () => {
    const service = useOutletContext<Service>();
    return <ServiceBookingsTab service={service} />;
};

const BusinessBalanceRoute = () => {
    const business = useOutletContext<Business>();
    return <BalanceTab business={business} />;
};

const BusinessPaymentsRoute = () => {
    const business = useOutletContext<Business>();
    return <BusinessPaymentsTab business={business} />;
};

const BusinessReservationsAsClientRoute = () => {
    const business = useOutletContext<Business>();
    return <BusinessReservationsAsClientTab business={business} />;
};

const BusinessBookingsAsProviderRoute = () => {
    const business = useOutletContext<Business>();
    return <BusinessBookingsAsProviderTab business={business} />;
};

const BusinessDeletedReservationsRoute = () => {
    const business = useOutletContext<Business>();
    return <BusinessDeletedReservationsTab business={business} />;
};

const BusinessProviderServicesRoute = () => {
    const business = useOutletContext<Business>();
    return <ProviderServices businessId={String(business.id)} active={true} />;
};

const BusinessDetailsRoute = () => {
    const business = useOutletContext<Business>();
    const updateBusinessMutation = useUpdateBusiness();
    return <EditBusinessDetails business={business} updateMutation={updateBusinessMutation} />;
};

const BusinessUsersRoute = () => {
    const business = useOutletContext<Business>();
    const updateBusinessMutation = useUpdateBusiness();
    return <BusinessUsersTab business={business} updateMutation={updateBusinessMutation} />;
};

const BusinessEmailsRoute = () => {
    const business = useOutletContext<Business>();
    return <BusinessEmailsTab business={business} />;
};

const BusinessCommercialRoute = () => {
    const business = useOutletContext<Business>();
    return <BusinessCommercialTab business={business} />;
};

const PersonReservationsRoute = () => {
    const person = useOutletContext<Person>();
    return <PersonReservationsTab person={person} />;
};

const PersonDetailsRoute = () => {
    const person = useOutletContext<Person>();
    return <PersonDetailsTab person={person} />;
};

const ProtectedRoute = ({ children }: { children: React.ReactNode }): JSX.Element => {
    if (!isAuthenticated()) {
        return <Navigate to={LOGIN_PATH} replace />;
    }
    return <>{children}</>;
};

export default function AppRoutes(): JSX.Element {
    const navigate = useNavigate();

    useEffect(() => {
        initNavigate(navigate);
    }, [navigate]);

    const PublicRoutes = [
        { path: LOGIN_PATH, element: <LoginPage /> },
        { path: ONBOARDING_PATH, element: <OnboardingPage /> },
        { path: CREATE_ACCOUNT_PATH, element: <CreateAccountPage /> },
        { path: '/google-callback', element: <GoogleCallbackPage /> },
    ]

    // No left menu or top navbar
    const PrivateRoutesNoWrapper = [
        { path: MANIFEST_SERVICE_ORDER_PATH, element: <ManifestServiceOrderPage /> },
        { path: RESERVATION_PRINT_PATH, element: <ReservationPrintPage /> },
    ]

    const PrivateRoutes = [

        // HOME PATH
        { path: ADMIN_HOME_PATH, element: <HomePage /> },

        { path: PERSONS_PATH, element: <PaxsPage /> },
        { path: METRICS_PATH, element: <MetricsPage /> },
        { path: SERVICE_BOOKING_PATH, element: <ServiceBookingPage /> },
        { path: MONEY_TRANSACTIONS_PATH, element: <MoneyTransactionsPage /> },
        { path: MONEY_TRANSACTIONS_IN_PATH, element: <MoneyTransactionsInPage /> },
        { path: MONEY_TRANSACTIONS_OUT_PATH, element: <MoneyTransactionsOutPage /> },
        { path: SUBSCRIPTIONS_PATH, element: <SubscriptionsPage /> },
        { path: `${SUBSCRIPTIONS_PATH}/:id`, element: <EditSubscriptionPage /> },
        { path: FINANCES_PATH, element: <FinancesPage /> },
        { path: RESERVATION_PATH, element: <ReservationPage /> },
        { path: RESERVATIONS_PATH, element: <ReservationsExportPage /> },
        { path: RESERVATIONS_EXPORT_PATH, element: <ReservationsExportPage /> },
        { path: SERVICE_ORDERS_PATH, element: <ServiceOrdersPage /> },
        { path: EDIT_SERVICE_ORDER_PATH, element: <EditServiceOrderPage /> },
        { path: SERVICES_PATH, element: <ServicesListPage /> },
        { path: PRODUCTS_PATH, element: <ProductsPage /> },
        { path: PRODUCT_PATH, element: <ProductEditPage /> },
        { path: TARIFFS_PATH, element: <TariffsPage /> },
        {
            path: `${SERVICE_PATH}/*`, element: <ServicePage />, children: [
                { index: true, element: <Navigate to="details" replace /> },
                { path: 'details', element: <ServiceDetailsRoute /> },
                { path: 'operations', element: <ServiceOperationsRoute /> },
                { path: 'providers', element: <ServiceProvidersRoute /> },
                { path: 'prices', element: <ServicePricesRoute /> },
                { path: 'costs', element: <ServiceCostsRoute /> },
                { path: 'prices_and_costs', element: <ServicePricesAndCostsRoute /> },
                { path: 'margins', element: <Navigate to="prices_and_costs" replace /> },
                { path: 'images', element: <ServiceImagesRoute /> },
                { path: 'discounts', element: <ServiceDiscountsRoute /> },
                { path: 'booking_limits', element: <ServiceLimitsRoute /> },
                { path: 'price_generator', element: <ServicePriceGeneratorRoute /> },
                { path: 'bookings', element: <ServiceBookingsRoute /> },
                { path: '*', element: <Navigate to="details" replace /> },
            ]
        },
        { path: SERVICE_GROUPS_PATH, element: <ServiceGroupsPage /> },
        { path: SERVICE_GROUP_PATH, element: <EditServiceGroupPage /> },
        { path: BUSINESSES_PATH, element: <ListBusinessessPage /> },
        { path: MY_BUSINESSES_PATH, element: <HomePage /> },
        {
            path: `${BUSINESS_PATH}/*`, element: <EditBusinessPage />, children: [
                { index: true, element: <Navigate to="details" replace /> },
                { path: 'details', element: <BusinessDetailsRoute /> },
                { path: 'balance', element: <BusinessBalanceRoute /> },
                { path: 'payments', element: <BusinessPaymentsRoute /> },
                { path: 'reservations_as_client', element: <BusinessReservationsAsClientRoute /> },
                { path: 'bookings_as_provider', element: <BusinessBookingsAsProviderRoute /> },
                { path: 'users', element: <BusinessUsersRoute /> },
                { path: 'emails', element: <BusinessEmailsRoute /> },
                { path: 'commercial', element: <BusinessCommercialRoute /> },
                { path: 'provider_services', element: <BusinessProviderServicesRoute /> },
                { path: 'deleted_reservations', element: <BusinessDeletedReservationsRoute /> },
                { path: '*', element: <Navigate to="details" replace /> },
            ]
        },
        {
            path: `${PERSON_PATH}/*`, element: <PersonPage />, children: [
                { index: true, element: <Navigate to="details" replace /> },
                { path: 'details', element: <PersonDetailsRoute /> },
                { path: 'reservations_as_pax', element: <PersonReservationsRoute /> },
                { path: '*', element: <Navigate to="details" replace /> },
            ]
        },
        { path: INVENTORY_ITEMS_PATH, element: <InventoryItemsPage /> },
        { path: OPERATIONS_PATH, element: <ServiceOrdersMatrixPage /> },
        { path: FLIGHTS_PATH, element: <FlightsPage /> },

        { path: HISTORY_PATH, element: <HistoryPage /> },
        { path: AI_CHAT_PATH, element: <AiChatPage /> },
        { path: SETTINGS_USERS_PATH, element: <UsersPage /> },
        {
            path: `${SETTINGS_USER_EDIT_PATH}/*`,
            element: <EditUserPage />,
            children: [
                { index: true, element: <Navigate to="details" replace /> },
                { path: 'details', element: <UserDetailsTab /> },
                { path: 'menu', element: <UserMenuPermissionsTab /> },
                { path: 'create', element: <UserCreatePermissionsTab /> },
                { path: 'reservations', element: <UserReservationPermissionsTab /> },
                { path: 'business_scope', element: <Navigate to="reservations" replace /> },
                { path: 'edit_scope', element: <Navigate to="reservations" replace /> },
                { path: '*', element: <Navigate to="details" replace /> },
            ]
        },
        { path: TICKETS_PATH, element: <TicketsPage /> },
        { path: TICKET_PATH, element: <EditTicketPage /> },
        { path: EMAIL_CAMPAIGNS_PATH, element: <EmailCampaignsPage /> },
        { path: EMAIL_TEMPLATES_PATH, element: <EmailTemplatesPage /> },
        { path: EMAIL_TEMPLATE_PATH, element: <EditEmailTemplatePage /> },

        // Settings
        { path: SETTINGS_PATH, element: <TenantSettingsPage /> },
        { path: SETTINGS_ONLINE_STORE_PATH, element: <SettingsOnlineStorePage /> },
        { path: SETTINGS_LANGUAGE_PATH, element: <SettingsLanguagePage /> },
        { path: SETTINGS_EXCHANGE_RATES_PATH, element: <SettingsExchangeRatesPage /> },
        { path: SETTINGS_DISCOUNT_CATEGORIES_PATH, element: <SettingsDiscountCategoriesPage /> },
        { path: SETTINGS_SERVICE_ORDERS_PATH, element: <SettingsServiceOrdersPage /> },
        { path: SETTINGS_CHANGE_PASSWORD_PATH, element: <SettingsChangePasswordPage /> },
        { path: SETTINGS_TAX_SETTINGS_PATH, element: <SettingsTaxSettingsPage /> },
        { path: SETTINGS_NOTIFICATIONS_PATH, element: <SettingsNotificationsPage /> },
        { path: SETTINGS_EMAILS_PATH, element: <SettingsEmailsPage /> },
        { path: SETTINGS_GMAIL_PATH, element: <SettingsGmailPage /> },
        { path: AFIP_HISTORY_PATH, element: <AfipHistoryPage /> },
        { path: AFIP_GENERATE_PATH, element: <GenerateInvoicePage /> },

        // Service Pages
        { path: PAYMENTS_PATH, element: <Navigate to={PAYMENTS_INCOME_PATH} replace /> },
        { path: PAYMENTS_INCOME_PATH, element: <PaymentsListPage direction="IN" /> },
        { path: PAYMENTS_OUTCOME_PATH, element: <PaymentsListPage direction="OUT" /> },
        { path: AGENDA_PATH, element: <AgendaPage /> },
        { path: `${PAYMENTS_PATH}/:id`, element: <EditPaymentPage /> },
        { path: BALANCE_PATH, element: <BalancePage /> },
        { path: ISSUES_PATH, element: <IssuesPage /> },
        { path: ASSIGNMENTS_PATH, element: <AssignmentsPage /> },
        // Catch-all route inside Wrapper
        { path: "*", element: <NotFoundPage /> }
    ]

    return (
        <Routes>

            {/* Public routes */}
            {PublicRoutes.map((route) => (
                <Route
                    key={route.path}
                    path={route.path}
                    element={route.element}
                />
            ))}

            {/* Private routes No Wrapper */}
            {PrivateRoutesNoWrapper.map((route) => (
                <Route
                    key={route.path}
                    path={route.path}
                    element={
                        <ProtectedRoute>
                            {route.element}
                        </ProtectedRoute>
                    }
                />
            ))}

            {PrivateRoutes.map((route) => (
                <Route
                    key={route.path}
                    path={route.path}
                    element={
                        <ProtectedRoute>
                            <Wrapper>
                                {route.element}
                            </Wrapper>
                        </ProtectedRoute>
                    }
                >
                    {route.children?.map((child) => (
                        <Route
                            key={child.path || 'index'}
                            index={child.index}
                            path={child.path}
                            element={child.element}
                        />
                    ))}
                </Route>
            ))}
        </Routes>
    );
};
